import { useReducer, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { carouselReducer, getSiblings } from './reducer';

function threshold(target) {
  const width = target.clientWidth;
  return width / 5;
}

const initialCarouselState = {
  offset: 0,
  desired: 0,
  active: 0
};

function swiped(e, dispatch, indicators, dir, slidesPerView) {
  const t = threshold(e.event.target);
  const d = dir * -e.deltaX;

  if (d >= t) {
    dispatch({
      type: dir > 0 ? 'next' : 'prev',
      indicators,
      slidesPerView
    });
  } else {
    dispatch({
      type: 'drag',
      offset: 0
    });
  }
}

export const calcPageWidth = (length, slidesPerView, showPartial) => {
  // partial shows 1/3 max 10% of page
  const pageWidth = 100;
  let parcialWidth = 100 / slidesPerView / 3;

  if (parcialWidth > 10) {
    parcialWidth = 10;
  }

  return showPartial && length > 1 ? pageWidth - parcialWidth : pageWidth;
};
export default function useCarousel({
  length,
  interval,
  transitionDuration,
  auto,
  slidesPerView,
  showPartial,
  showPartialNextSlide,
  lazy
}) {
  const pageWidth = calcPageWidth(length, slidesPerView, showPartial || showPartialNextSlide);
  const cardWidth = `${pageWidth / slidesPerView}%`;
  const indicators = Math.ceil(length / slidesPerView);
  const smooth = `transform ${transitionDuration}ms ease`;
  const [state, dispatch] = useReducer(carouselReducer, Object.assign({}, initialCarouselState, {
    visible: getSiblings({
      lazy,
      length,
      active: 0,
      slidesPerView
    })
  }));
  const handlers = useSwipeable({
    onSwiping(e) {
      dispatch({
        type: 'drag',
        offset: e.deltaX
      });
    },

    onSwipedLeft(e) {
      swiped(e, dispatch, indicators, 1, slidesPerView);
    },

    onSwipedRight(e) {
      swiped(e, dispatch, indicators, -1, slidesPerView);
    },

    trackMouse: false,
    trackTouch: true
  });
  useEffect(() => {
    const id = auto && setTimeout(() => dispatch({
      type: 'next',
      indicators,
      slidesPerView
    }), interval);
    return () => clearTimeout(id);
  }, [auto, state.offset, state.active]);
  useEffect(() => {
    const id = setTimeout(() => dispatch({
      type: 'done',
      lazy,
      length,
      slidesPerView
    }), transitionDuration);
    return () => clearTimeout(id);
  }, [state.desired]);
  const partialSize = showPartial ? (100 - pageWidth) / 2 : 0;
  const partialPrev = state.active !== 0 ? partialSize : 0;
  const style = {
    transform: 'translateX(0)',
    width: `${pageWidth * (length / slidesPerView)}%`,
    left: `-${state.active * (pageWidth / slidesPerView) - partialPrev}%`
  };

  if (state.desired !== state.active) {
    const dist = Math.abs(state.active - state.desired) / slidesPerView;
    const pref = Math.sign(state.offset || 0);
    const dir = -1 * Math.sign(state.desired - state.active);
    const shift = 100 * (pref || dir) / (length / slidesPerView) * dist;
    let shiftCompensation = 0;

    if (state.active === 0) {
      shiftCompensation = partialSize / length / slidesPerView;
    }

    if (state.desired === 0) {
      shiftCompensation = partialSize / (length / slidesPerView) * -1;
    }

    style.transition = smooth;
    style.transform = `translateX(${shift + shiftCompensation}%)`;
  } else if (!Number.isNaN(state.offset)) {
    style.transform = `translateX(${state.offset}px)`;
  }

  return {
    active: state.active,
    setActive: n => dispatch({
      type: 'jump',
      desired: n,
      slidesPerView
    }),
    handlers,
    style,
    cardWidth,
    indicators,
    visible: state.visible
  };
}